import { useQueryClient } from '@tanstack/react-query'
import { Bell, Globe, MessageCircle, Shield } from 'lucide-react'
import { useEffect, useState, type MouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  IconButton,
  Logo,
  Skeleton,
} from '@/components/ui'
import { CommunityIcon } from '@/components/common/CommunityIcon'
import { cn } from '@/components/ui/cn'
import { isVisitorPreview } from '@/features/auth/landing'
import { track } from '@/lib/analytics'
import { loginPath, saveUserPreference, useAuth, useMembership } from '@/lib/auth'
import { communitySlugFromPath, useCommunity, type CommunityTabKey } from '@/lib/community'
import { HAS_LANGUAGE_CHOICE, LANGUAGES, setLanguage, t, useI18n } from '@/lib/i18n'
import { CommunitySwitcher } from './CommunitySwitcher'
import { SearchBox } from './SearchBox'
import { canBrowseCommunity } from './shell-utils'
import { SingleSlot } from './slots'
import { ThemeToggle } from './ThemeToggle'
import { UserMenu } from './UserMenu'

/** The MG logomark and "AI School" (REDESIGN section 9; the mark is the MG); the narrowest phones get the mark only. */
function PlatformMark() {
  return (
    <>
      <Logo className="max-[359px]:hidden" />
      <Logo compact className="min-[360px]:hidden" />
    </>
  )
}

/** MG AI School logo linking home (visitors outside a community, or when a community cannot be shown). It is a
 * 40 px tap target on phones too (UI.md section 10), not just the 34 px artwork (E2E-J10-01). */
function PlatformLogo() {
  return (
    <Link to="/" aria-label={t('MG AI School home')} className="focus-ring flex min-h-10 items-center rounded-control">
      <PlatformMark />
    </Link>
  )
}

/** Members outside a community: the logo with the switcher chevron (REDESIGN section 9, like Skool). */
function PlatformSwitcher() {
  return (
    <CommunitySwitcher currentSlug={null}>
      <PlatformMark />
    </CommunitySwitcher>
  )
}

/** Left side inside a community: icon 40, name 17/650 and, for logged in people, the switcher chevron. */
function CommunityIdentity({ slug }: { slug: string }) {
  const { me, isLoggedIn } = useAuth()
  const { community, isError } = useCommunity(slug)
  const mine = me?.communities.find((c) => c.slug.toLowerCase() === slug.toLowerCase())
  const name = community?.name ?? mine?.name
  const iconUrl = community?.iconUrl ?? mine?.iconUrl ?? null
  // A community that cannot be loaded shows the platform logo, never a name made up from the URL (Q-TRUST-09).
  if (!name && isError) return <PlatformLogo />
  const identity = (
    <span className="flex min-w-0 items-center gap-3">
      {name ? (
        <CommunityIcon name={name} src={iconUrl} id={community?.id ?? mine?.id} brandColor={community?.brandColor ?? mine?.brandColor} size={40} />
      ) : (
        <Skeleton width={40} height={40} />
      )}
      {name ? (
        <span className="truncate text-[17px] leading-6 font-650 tracking-[-0.01em] text-text">{name}</span>
      ) : (
        <Skeleton height={16} width={120} />
      )}
    </span>
  )
  // Visitors see the community name only (REDESIGN section 1): no switcher until they log in.
  if (!isLoggedIn) {
    return (
      <Link to={`/${community?.slug ?? slug}`} className="focus-ring flex min-w-0 items-center rounded-control py-1 pr-1">
        {identity}
      </Link>
    )
  }
  return <CommunitySwitcher currentSlug={slug}>{identity}</CommunitySwitcher>
}

/** Language globe (I18N-003). For members the choice is saved to their account too, like the avatar menu. */
function LanguagePicker() {
  const { lang } = useI18n()
  const { isLoggedIn } = useAuth()
  const qc = useQueryClient()
  const onLanguage = (value: string) => {
    track('language_changed', { from: lang, to: value, surface: 'top_bar' })
    void setLanguage(value)
    if (isLoggedIn) void saveUserPreference(qc, { language: value })
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <IconButton aria-label={t('Language')} icon={<Globe />} size={40} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] w-56">
        <DropdownMenuRadioGroup value={lang} onValueChange={onLanguage}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem key={l.code} value={l.code} lang={l.code}>
              {l.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Chat and bell buttons with live counts; S7 popovers wrap them when present. */
function InboxButtons() {
  const { me } = useAuth()
  const navigate = useNavigate()
  const chats = me?.unread.chats ?? 0
  const notifications = me?.unread.notifications ?? 0
  const chatLabel = chats ? t('Chats, {count} unread', { count: chats }) : t('Chats')
  const bellLabel = notifications ? t('Notifications, {count} unread', { count: notifications }) : t('Notifications')
  // A popover slot wraps the button in a Radix trigger, which adds aria-haspopup: then the popover
  // handles the click. Without one (or with a placeholder slot) the button opens the full page.
  const go = (path: string) => (e: MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasAttribute('aria-haspopup')) return
    navigate(path)
  }
  const chatButton = <IconButton aria-label={chatLabel} icon={<MessageCircle />} size={40} count={chats} onClick={go('/chat')} />
  const bellButton = (
    <IconButton aria-label={bellLabel} icon={<Bell />} size={40} count={notifications} onClick={go('/notifications')} />
  )
  return (
    <div className="hidden items-center gap-1 md:flex">
      <SingleSlot name="ChatPopover" props={{ children: chatButton }} fallback={chatButton} />
      <SingleSlot name="NotificationsPopover" props={{ children: bellButton }} fallback={bellButton} />
    </div>
  )
}

/**
 * Platform admins only (REDESIGN 10): the shield and "Admin" in the outline style, next to chat and the bell on
 * every page; phones show the shield alone (the label stays for screen readers).
 */
function AdminButton() {
  const { pathname } = useLocation()
  const here = pathname === '/admin' || pathname.startsWith('/admin/')
  return (
    <Button asChild variant="outline" size="sm" className={cn('mx-1 max-sm:w-10 max-sm:px-0', here && 'bg-surface-2 text-text')}>
      <Link to="/admin" aria-current={here ? 'page' : undefined} data-testid="topbar-admin">
        <Shield aria-hidden="true" />
        <span className="max-sm:sr-only">{t('Admin')}</span>
      </Link>
    </Button>
  )
}

/**
 * Inside a community the top bar and the tab row read as one white band (Skool): the bar's bottom line only
 * shows once the page scrolls past the tab row, so the sticky bar still separates from the content below.
 */
function useScrolledPast(px: number, enabled: boolean): boolean {
  const [past, setPast] = useState(false)
  useEffect(() => {
    if (!enabled) return
    const update = () => setPast(window.scrollY > px)
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [px, enabled])
  return enabled && past
}

export interface TopBarProps {
  /** Community slug when the page is inside a community, else null. */
  communitySlug: string | null
  /** Active community tab (drives the search placeholder). */
  tab: CommunityTabKey | null
}

/**
 * Sticky 64 px header, Skool style (REDESIGN sections 4 and 9): identity on the left (community icon and name,
 * or the MG logomark and "MG AI School", with the switcher chevron for members), a wide search inside a
 * community, then the globe, the theme toggle, chat and bell (red counts) and the avatar; platform admins also
 * get the Admin button (REDESIGN 10). Logged out: the globe, the theme toggle and an outlined LOG IN only. The
 * admin's "View site as a visitor" home (`/?preview=visitor`) gets the logged out bar too, exactly what a visitor
 * sees (Q-ADMIN-02).
 */
export function TopBar({ communitySlug, tab }: TopBarProps) {
  const { isLoggedIn: signedIn, isLoading, isPlatformAdmin } = useAuth()
  const location = useLocation()
  const isLoggedIn = signedIn && !(isPlatformAdmin && location.pathname === '/' && isVisitorPreview(location.search))
  const { community } = useCommunity(communitySlug ?? undefined)
  const { isMember } = useMembership(communitySlug ?? undefined)
  // Visitors locked out of a private community get the identity and LOG IN only, like Skool: no search and no
  // tab row (CommunityLayout) until they can see the content (Q-FAM-05).
  const browsable = !!communitySlug && canBrowseCommunity(community, { isMember, isPlatformAdmin })
  const next = location.pathname === '/' ? undefined : location.pathname + location.search
  const scrolled = useScrolledPast(44, browsable)
  // An unknown community URL keeps the plain logo link, no switcher (Q-TRUST-09).
  const unknownCommunity = !communitySlug && !!communitySlugFromPath(location.pathname)
  // Visitors see the globe on phones too (I18N-003: a picker when logged out), inside a community from 375 px up,
  // where the community name still fits beside search, the globe, the theme and LOG IN (UXA-22; at 360 px "MG AI"
  // would be cut to "MG..."). Members on phones pick the language from the avatar menu.
  const globe = HAS_LANGUAGE_CHOICE ? (
    <div className={isLoggedIn ? 'hidden md:block' : communitySlug ? 'hidden min-[375px]:block' : undefined}>
      <LanguagePicker />
    </div>
  ) : null
  // Members on phones switch the theme from the avatar menu; visitors always get the button.
  const themeToggle = (
    <div className={isLoggedIn ? 'hidden md:block' : undefined}>
      <ThemeToggle />
    </div>
  )
  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b bg-surface transition-[border-color] duration-150',
        browsable && !scrolled ? 'border-transparent' : 'border-line',
      )}
    >
      <div className="relative mx-auto flex h-16 w-full max-w-[1080px] items-center gap-3 px-4 md:grid md:grid-cols-[minmax(0,max-content)_minmax(0,1fr)_max-content] md:gap-6 lg:px-6">
        <div className="flex min-w-0 flex-1 items-center md:max-w-[340px]">
          {communitySlug ? (
            <CommunityIdentity slug={communitySlug} />
          ) : isLoggedIn && !unknownCommunity ? (
            <PlatformSwitcher />
          ) : (
            <PlatformLogo />
          )}
        </div>
        {/* Search lives inside a community (one creator, no directory to search outside of one). */}
        <div className="flex justify-center">{browsable ? <SearchBox slug={communitySlug} tab={tab} /> : null}</div>
        <div className="flex shrink-0 items-center justify-end gap-1">
          {isLoading ? (
            <Skeleton shape="circle" width={32} height={32} className="mx-1" />
          ) : isLoggedIn ? (
            <>
              {globe}
              {themeToggle}
              {isPlatformAdmin ? <AdminButton /> : null}
              <InboxButtons />
              <UserMenu communitySlug={communitySlug} />
            </>
          ) : (
            <>
              {globe}
              {themeToggle}
              <Button asChild variant="outline" className="ml-1 max-sm:px-3.5">
                <Link to={loginPath(next)}>{t('Log in')}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
